import { ref, onMounted, onUnmounted, watch } from 'vue';

export interface AudioDevice {
  deviceId: string;
  label: string;
  kind: 'audioinput' | 'audiooutput';
}

const STORAGE_KEY = 'humaniz_audio_devices';

export function useAudioDevices() {
  const audioInputs = ref<AudioDevice[]>([]);
  const audioOutputs = ref<AudioDevice[]>([]);
  const selectedMicrophoneId = ref<string>('default');
  const selectedSpeakerId = ref<string>('default');
  const microphoneLevel = ref<number>(0);
  const isMicrophoneLevelActive = ref<boolean>(false);
  
  let audioContext: AudioContext | null = null;
  let analyser: AnalyserNode | null = null;
  let microphone: MediaStreamAudioSourceNode | null = null;
  let animationId: number | null = null;
  let microphoneStream: MediaStream | null = null;

  const loadSavedDevices = () => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed.microphoneId) selectedMicrophoneId.value = parsed.microphoneId;
        if (parsed.speakerId) selectedSpeakerId.value = parsed.speakerId;
      }
    } catch (error) {
      console.error('Error loading saved audio devices:', error);
    }
  };

  const saveSelectedDevices = () => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        microphoneId: selectedMicrophoneId.value,
        speakerId: selectedSpeakerId.value
      }));
    } catch (error) {
      console.error('Error saving audio devices:', error);
    }
  };

  const enumerateDevices = async () => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      
      audioInputs.value = [
        { deviceId: 'default', label: 'System Default Microphone', kind: 'audioinput' },
        ...devices
          .filter(device => device.kind === 'audioinput' && device.deviceId !== 'default')
          .map(device => ({
            deviceId: device.deviceId,
            label: device.label || `Microphone ${device.deviceId.slice(0, 5)}`,
            kind: 'audioinput' as const
          }))
      ];
      
      audioOutputs.value = [
        { deviceId: 'default', label: 'System Default Speaker', kind: 'audiooutput' },
        ...devices
          .filter(device => device.kind === 'audiooutput' && device.deviceId !== 'default')
          .map(device => ({
            deviceId: device.deviceId,
            label: device.label || `Speaker ${device.deviceId.slice(0, 5)}`,
            kind: 'audiooutput' as const
          }))
      ];
    } catch (error) {
      console.error('Error enumerating devices:', error);
    }
  };

  const startMicrophoneLevel = async () => {
    if (isMicrophoneLevelActive.value) return;
    
    try {
      const constraints: MediaStreamConstraints = {
        audio: selectedMicrophoneId.value !== 'default' 
          ? { deviceId: { exact: selectedMicrophoneId.value } }
          : true,
        video: false
      };
      
      microphoneStream = await navigator.mediaDevices.getUserMedia(constraints);
      audioContext = new AudioContext();
      analyser = audioContext.createAnalyser();
      microphone = audioContext.createMediaStreamSource(microphoneStream);
      
      analyser.fftSize = 256;
      const bufferLength = analyser.frequencyBinCount;
      const dataArray = new Uint8Array(bufferLength);
      
      microphone.connect(analyser);
      
      isMicrophoneLevelActive.value = true;
      
      const updateLevel = () => {
        if (!isMicrophoneLevelActive.value) return;
        
        analyser!.getByteFrequencyData(dataArray);
        const average = dataArray.reduce((acc, val) => acc + val, 0) / bufferLength;
        microphoneLevel.value = Math.min(100, (average / 128) * 100);
        
        animationId = requestAnimationFrame(updateLevel);
      };
      
      updateLevel();
    } catch (error) {
      console.error('Error starting microphone level monitoring:', error);
    }
  };

  const stopMicrophoneLevel = () => {
    isMicrophoneLevelActive.value = false;
    
    if (animationId) {
      cancelAnimationFrame(animationId);
      animationId = null;
    }
    
    if (microphoneStream) {
      microphoneStream.getTracks().forEach(track => track.stop());
      microphoneStream = null;
    }
    
    if (microphone) {
      microphone.disconnect();
      microphone = null;
    }
    
    if (audioContext) {
      audioContext.close();
      audioContext = null;
    }
    
    microphoneLevel.value = 0;
  };

  const testSpeaker = async () => {
    try {
      const audioContext = new AudioContext();
      
      // Create a simple chime sound
      const duration = 0.3;
      const frequency1 = 523.25; // C5
      const frequency2 = 659.25; // E5
      const frequency3 = 783.99; // G5
      
      const playTone = (freq: number, startTime: number) => {
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();
        
        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);
        
        oscillator.frequency.value = freq;
        oscillator.type = 'sine';
        
        gainNode.gain.setValueAtTime(0, startTime);
        gainNode.gain.linearRampToValueAtTime(0.3, startTime + 0.01);
        gainNode.gain.exponentialRampToValueAtTime(0.01, startTime + duration);
        
        oscillator.start(startTime);
        oscillator.stop(startTime + duration);
      };
      
      const now = audioContext.currentTime;
      playTone(frequency1, now);
      playTone(frequency2, now + 0.15);
      playTone(frequency3, now + 0.3);
      
      // Set the output device if supported
      if (selectedSpeakerId.value !== 'default' && 'setSinkId' in AudioContext.prototype) {
        await (audioContext as any).setSinkId(selectedSpeakerId.value);
      }
    } catch (error) {
      console.error('Error playing test sound:', error);
    }
  };

  const getConstraintsForMicrophone = () => {
    if (selectedMicrophoneId.value === 'default') {
      return {
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
        video: false
      };
    }
    
    return {
      audio: {
        deviceId: { exact: selectedMicrophoneId.value },
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
      },
      video: false
    };
  };

  // Watch for device changes
  watch([selectedMicrophoneId, selectedSpeakerId], () => {
    saveSelectedDevices();
  });

  onMounted(async () => {
    loadSavedDevices();
    await enumerateDevices();
    
    // Listen for device changes
    navigator.mediaDevices.addEventListener('devicechange', enumerateDevices);
  });

  onUnmounted(() => {
    stopMicrophoneLevel();
    navigator.mediaDevices.removeEventListener('devicechange', enumerateDevices);
  });

  return {
    audioInputs,
    audioOutputs,
    selectedMicrophoneId,
    selectedSpeakerId,
    microphoneLevel,
    isMicrophoneLevelActive,
    startMicrophoneLevel,
    stopMicrophoneLevel,
    testSpeaker,
    getConstraintsForMicrophone,
    enumerateDevices
  };
}